'use client';
import React, { useState, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { BarChart2, Download, ArrowUpDown, Search, AlertTriangle, Building2, TrendingDown, TrendingUp, Filter, RefreshCw, FileText, ChevronDown, ChevronUp, CreditCard } from 'lucide-react';
import { useAppStore } from '@/lib/store';
import { mockComparisonReport } from '@/lib/mockData';
import { InstitutionComparison, LoanComparison } from '@/lib/types';
import MetricCard from '@/components/ui/MetricCard';
import StatusBadge from '@/components/ui/StatusBadge';
import EmptyState from '@/components/ui/EmptyState';
import { formatEuro, formatEuroCompact, formatPercent, formatDate } from '@/lib/formatters';
import PaymentsBarChart from './PaymentsBarChart';
import BalanceEvolutionChart from './BalanceEvolutionChart';
import Link from 'next/link';

type SortKey = 'nome' | 'saldoAnterior' | 'saldoAtual' | 'valorPago' | 'variacaoPercent' | 'numEmprestimosAtual';
type SortDir = 'asc' | 'desc';
type FilterStatus = 'all' | 'match' | 'anomalia' | 'novo' | 'ausente';

export default function ComparisonReportContent() {
  const router = useRouter();
  const { report: storeReport, setSelectedInstitution } = useAppStore();
  const report = storeReport ?? mockComparisonReport;

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('valorPago');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [exporting, setExporting] = useState(false);
  const [expandedBanks, setExpandedBanks] = useState<Set<string>>(new Set());

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const filtered = useMemo(() => {
    let rows = [...report.institutions];
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((r) => r.nome.toLowerCase().includes(q));
    }
    if (filterStatus !== 'all') {
      rows = rows.filter((r) => r.status === filterStatus);
    }
    rows.sort((a, b) => {
      const av = a[sortKey] as number | string;
      const bv = b[sortKey] as number | string;
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv, 'pt') : bv.localeCompare(av, 'pt');
      }
      return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
    return rows;
  }, [report.institutions, search, sortKey, sortDir, filterStatus]);

  const handleExport = () => {
    setExporting(true);
    setTimeout(() => setExporting(false), 1200);
  };

  const handleInstitutionClick = (inst: InstitutionComparison) => {
    setSelectedInstitution(inst.institutionId);
    router.push('/institution-detail');
  };

  const toggleBank = (id: string) => {
    setExpandedBanks((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpandedBanks(new Set(report.institutions.map((i) => i.institutionId)));
  const collapseAll = () => setExpandedBanks(new Set());

  const anomalias = report.institutions.filter((i) => i.status === 'anomalia').length;

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Relatório Comparativo</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {report.mesAnterior} → {report.mesAtual} · Gerado em {new Date().toLocaleDateString('pt-PT')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border bg-card rounded-lg hover:bg-muted transition-all duration-150 disabled:opacity-60"
          >
            {exporting ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
            {exporting ? 'A exportar...' : 'Exportar Excel'}
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border bg-card rounded-lg hover:bg-muted transition-all duration-150 disabled:opacity-60"
          >
            <FileText size={14} />
            Exportar PDF
          </button>
        </div>
      </div>

      {/* Anomalia alert */}
      {anomalias > 0 && (
        <div className="flex items-start gap-3 bg-warning-subtle border border-amber-200 rounded-lg px-4 py-3 mb-6">
          <AlertTriangle size={16} className="text-warning-color mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-warning-color">
              {anomalias} {anomalias === 1 ? 'instituição requer' : 'instituições requerem'} atenção
            </p>
            <p className="text-xs text-warning-color/80 mt-0.5">
              Foram detectadas anomalias: saldo sem variação ou aumento inesperado de dívida. Verifique os detalhes abaixo.
            </p>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <MetricCard
          label="Saldo Total (Mês 1)"
          value={formatEuroCompact(report.totalSaldoAnterior)}
          subValue={report.mesAnterior}
          trend="neutral"
          icon={<BarChart2 size={18} />}
        />
        <MetricCard
          label="Saldo Total (Mês 2)"
          value={formatEuroCompact(report.totalSaldoAtual)}
          subValue={report.mesAtual}
          trend="neutral"
          icon={<BarChart2 size={18} />}
        />
        <MetricCard
          label="Total Pago no Mês"
          value={formatEuroCompact(report.totalValorPago)}
          subValue={`${formatPercent(((report.totalSaldoAnterior - report.totalSaldoAtual) / report.totalSaldoAnterior) * 100)} do saldo anterior`}
          trend="positive"
          trendLabel="Redução de dívida"
          icon={<TrendingDown size={18} />}
        />
        <MetricCard
          label="Instituições"
          value={`${report.numInstituicoes}`}
          subValue={`${report.numInstituicoesMatch} coincidem · ${anomalias} anomalias`}
          trend={anomalias > 0 ? 'warning' : 'positive'}
          trendLabel={anomalias > 0 ? `${anomalias} anomalia(s) detectada(s)` : 'Todas reconciliadas'}
          icon={<Building2 size={18} />}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
        <div className="xl:col-span-2 bg-card border border-border rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Valor Pago por Instituição</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Diferença entre mês 1 e mês 2 (€)</p>
            </div>
          </div>
          <PaymentsBarChart data={report.institutions} />
        </div>
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Evolução de Saldos</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Comparação entre os dois meses</p>
            </div>
          </div>
          <BalanceEvolutionChart
            mesAnterior={report.mesAnterior}
            mesAtual={report.mesAtual}
            totalAnterior={report.totalSaldoAnterior}
            totalAtual={report.totalSaldoAtual}
            totalPago={report.totalValorPago}
          />
        </div>
      </div>

      {/* Institution summary table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-5 py-4 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Building2 size={16} className="text-primary" />
            Mapa por Instituição
            <span className="text-xs font-normal text-muted-foreground ml-1">({filtered.length} de {report.institutions.length})</span>
          </h2>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Pesquisar instituição..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 pr-3 py-2 text-sm bg-input border border-border rounded-lg w-full sm:w-52 focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring"
              />
            </div>
            <div className="relative">
              <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
                className="pl-8 pr-8 py-2 text-sm bg-input border border-border rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-ring/30 cursor-pointer"
              >
                <option value="all">Todos os estados</option>
                <option value="match">Coincide</option>
                <option value="anomalia">Anomalia</option>
                <option value="novo">Novo</option>
                <option value="ausente">Ausente</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/30 border-b border-border">
                <SortableHeader label="Instituição" sortKey="nome" current={sortKey} dir={sortDir} onSort={handleSort} align="left" />
                <SortableHeader label={`Saldo ${report.mesAnterior}`} sortKey="saldoAnterior" current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                <SortableHeader label={`Saldo ${report.mesAtual}`} sortKey="saldoAtual" current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                <SortableHeader label="Valor Pago" sortKey="valorPago" current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                <SortableHeader label="Variação" sortKey="variacaoPercent" current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                <SortableHeader label="Empréstimos" sortKey="numEmprestimosAtual" current={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                <th className="text-center px-4 py-3 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Estado</th>
                <th className="px-4 py-3 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Detalhe</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <EmptyState
                      icon={<Building2 size={22} />}
                      title="Nenhuma instituição encontrada"
                      description="Nenhuma instituição corresponde aos filtros seleccionados. Ajuste a pesquisa ou o filtro de estado."
                    />
                  </td>
                </tr>
              ) : (
                filtered.map((inst, idx) => (
                  <tr
                    key={`report-row-${inst.institutionId}`}
                    className={`border-b border-border hover:bg-muted/30 transition-colors duration-100 cursor-pointer
                      ${idx % 2 === 0 ? '' : 'bg-muted/10'}
                      ${inst.status === 'anomalia' ? 'bg-warning-subtle/40' : ''}
                    `}
                    onClick={() => handleInstitutionClick(inst)}
                  >
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                          <Building2 size={13} className="text-primary" />
                        </div>
                        <span className="font-medium text-foreground">{inst.nome}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-right tabular-nums text-muted-foreground">{formatEuro(inst.saldoAnterior)}</td>
                    <td className="px-4 py-3.5 text-right tabular-nums text-foreground font-medium">{formatEuro(inst.saldoAtual)}</td>
                    <td className="px-4 py-3.5 text-right">
                      <span className={`tabular-nums font-semibold ${inst.valorPago > 0 ? 'text-positive' : inst.valorPago < 0 ? 'text-negative' : 'text-muted-foreground'}`}>
                        {inst.valorPago >= 0 ? formatEuro(inst.valorPago) : `(${formatEuro(Math.abs(inst.valorPago))})`}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <div className={`flex items-center justify-end gap-1 tabular-nums font-medium text-sm
                        ${inst.variacaoPercent < 0 ? 'text-positive' : inst.variacaoPercent > 0 ? 'text-negative' : 'text-muted-foreground'}
                      `}>
                        {inst.variacaoPercent < 0 ? <TrendingDown size={13} /> : inst.variacaoPercent > 0 ? <TrendingUp size={13} /> : null}
                        {formatPercent(inst.variacaoPercent)}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-right tabular-nums text-muted-foreground">
                      {inst.numEmprestimosAtual}
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      <StatusBadge status={inst.status} />
                    </td>
                    <td className="px-4 py-3.5">
                      <span className="text-xs text-primary hover:underline font-medium">Ver detalhe →</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr className="bg-muted/40 border-t-2 border-border">
                  <td className="px-4 py-3.5 text-sm font-semibold text-foreground">
                    Total ({filtered.length} instituições)
                  </td>
                  <td className="px-4 py-3.5 text-right tabular-nums font-semibold text-foreground">
                    {formatEuro(filtered.reduce((s, r) => s + r.saldoAnterior, 0))}
                  </td>
                  <td className="px-4 py-3.5 text-right tabular-nums font-semibold text-foreground">
                    {formatEuro(filtered.reduce((s, r) => s + r.saldoAtual, 0))}
                  </td>
                  <td className="px-4 py-3.5 text-right tabular-nums font-bold text-positive">
                    {formatEuro(filtered.reduce((s, r) => s + r.valorPago, 0))}
                  </td>
                  <td colSpan={4} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* ── Per-bank loan comparison map ─────────────────────────────────── */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <CreditCard size={18} className="text-primary" />
              Mapa Comparativo de Empréstimos por Banco
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Comparação detalhada de cada empréstimo — {report.mesAnterior} vs {report.mesAtual}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              className="text-xs text-primary hover:underline font-medium px-2 py-1"
            >
              Expandir todos
            </button>
            <span className="text-muted-foreground text-xs">|</span>
            <button
              onClick={collapseAll}
              className="text-xs text-muted-foreground hover:text-foreground font-medium px-2 py-1"
            >
              Colapsar todos
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {report.institutions.map((inst) => {
            const isExpanded = expandedBanks.has(inst.institutionId);
            const loansPago = inst.loans.reduce((s, l) => s + (l.diferenca > 0 ? l.diferenca : 0), 0);

            return (
              <div
                key={`bank-map-${inst.institutionId}`}
                className={`bg-card border rounded-lg overflow-hidden transition-all duration-200
                  ${inst.status === 'anomalia' ? 'border-amber-300' : 'border-border'}
                `}
              >
                {/* Bank header row */}
                <button
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/30 transition-colors duration-150 text-left"
                  onClick={() => toggleBank(inst.institutionId)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Building2 size={15} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-foreground text-sm">{inst.nome}</span>
                        <StatusBadge status={inst.status} />
                        {inst.status === 'anomalia' && (
                          <AlertTriangle size={13} className="text-warning-color" />
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-0.5 flex-wrap">
                        <span className="text-xs text-muted-foreground">
                          {inst.loans.length} empréstimo{inst.loans.length !== 1 ? 's' : ''}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Saldo {report.mesAnterior}: <span className="tabular-nums">{formatEuro(inst.saldoAnterior)}</span>
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Saldo {report.mesAtual}: <span className="tabular-nums font-medium text-foreground">{formatEuro(inst.saldoAtual)}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 shrink-0 ml-4">
                    <div className="text-right hidden sm:block">
                      <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Valor Pago</p>
                      <p className={`text-sm font-bold tabular-nums ${inst.valorPago > 0 ? 'text-positive' : inst.valorPago < 0 ? 'text-negative' : 'text-muted-foreground'}`}>
                        {inst.valorPago >= 0 ? formatEuro(inst.valorPago) : `(${formatEuro(Math.abs(inst.valorPago))})`}
                      </p>
                    </div>
                    {isExpanded ? (
                      <ChevronUp size={16} className="text-muted-foreground" />
                    ) : (
                      <ChevronDown size={16} className="text-muted-foreground" />
                    )}
                  </div>
                </button>

                {/* Expanded loan table */}
                {isExpanded && (
                  <div className="border-t border-border">
                    {inst.loans.length === 0 ? (
                      <div className="px-5 py-6 text-center text-sm text-muted-foreground">
                        Nenhum empréstimo registado para esta instituição no período.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-muted/20 border-b border-border">
                              <th className="text-left px-5 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Produto Financeiro</th>
                              <th className="text-left px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Início</th>
                              <th className="text-left px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Fim</th>
                              <th className="text-right px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                                Total em Dívida<br />
                                <span className="normal-case font-normal">{report.mesAnterior}</span>
                              </th>
                              <th className="text-right px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                                Total em Dívida<br />
                                <span className="normal-case font-normal">{report.mesAtual}</span>
                              </th>
                              <th className="text-right px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Valor Pago</th>
                              <th className="text-center px-4 py-2.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Estado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {inst.loans.map((loan, lIdx) => (
                              <LoanRow
                                key={`loan-${inst.institutionId}-${loan.loanId}`}
                                loan={loan}
                                idx={lIdx}
                              />
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-muted/30 border-t-2 border-border">
                              <td colSpan={3} className="px-5 py-3 text-xs font-semibold text-foreground">
                                Total {inst.nome}
                              </td>
                              <td className="px-4 py-3 text-right tabular-nums text-sm font-semibold text-foreground">
                                {formatEuro(inst.loans.reduce((s, l) => s + (l.saldoAnterior ?? 0), 0))}
                              </td>
                              <td className="px-4 py-3 text-right tabular-nums text-sm font-semibold text-foreground">
                                {formatEuro(inst.loans.reduce((s, l) => s + (l.saldoAtual ?? 0), 0))}
                              </td>
                              <td className="px-4 py-3 text-right tabular-nums text-sm font-bold text-positive">
                                {formatEuro(inst.loans.reduce((s, l) => s + (l.diferenca > 0 ? l.diferenca : 0), 0))}
                              </td>
                              <td />
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Back to import */}
      <div className="mt-6 flex items-center gap-2">
        <Link href="/" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-150">
          ← Voltar à importação
        </Link>
      </div>
    </div>
  );
}

function LoanRow({ loan, idx }: { loan: LoanComparison; idx: number }) {
  const valorPago = loan.diferenca;
  const isNew = loan.status === 'novo';
  const isLiquidado = loan.status === 'liquidado';

  // When saldoAtual is 0 (or loan is liquidated) but there was a previous balance,
  // the full previous balance was paid — show it in the valor pago column.
  const hasPreviousBalance = (loan.saldoAnterior ?? 0) > 0;
  const currentIsZero = loan.saldoAtual === 0 || loan.saldoAtual === null;
  const showValorPago = hasPreviousBalance && currentIsZero;

  return (
    <tr className={`border-b border-border hover:bg-muted/20 transition-colors duration-100 ${idx % 2 === 0 ? '' : 'bg-muted/5'}`}>
      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          <CreditCard size={12} className="text-muted-foreground shrink-0" />
          <span className="font-medium text-foreground text-xs">{loan.produtoFinanceiro}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground tabular-nums whitespace-nowrap">
        {loan.inicio ? formatDate(loan.inicio) : '—'}
      </td>
      <td className="px-4 py-3 text-xs text-muted-foreground tabular-nums whitespace-nowrap">
        {loan.fim ? formatDate(loan.fim) : '—'}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-xs text-muted-foreground">
        {loan.saldoAnterior !== null ? formatEuro(loan.saldoAnterior) : <span className="italic">—</span>}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-xs font-medium text-foreground">
        {loan.saldoAtual !== null ? formatEuro(loan.saldoAtual) : <span className="italic text-muted-foreground">—</span>}
      </td>
      <td className="px-4 py-3 text-right">
        {isNew ? (
          <span className="text-xs text-muted-foreground italic">Novo</span>
        ) : showValorPago ? (
          <span className="tabular-nums text-xs font-semibold text-positive">
            {formatEuro(loan.saldoAnterior ?? 0)}
          </span>
        ) : (
          <span className={`tabular-nums text-xs font-semibold ${valorPago > 0 ? 'text-positive' : valorPago < 0 ? 'text-negative' : 'text-muted-foreground'}`}>
            {valorPago > 0 ? formatEuro(valorPago) : valorPago < 0 ? `(${formatEuro(Math.abs(valorPago))})` : '—'}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-center">
        <StatusBadge status={loan.status} />
      </td>
    </tr>
  );
}

function SortableHeader({
  label, sortKey, current, dir, onSort, align
}: {
  label: string;
  sortKey: SortKey;
  current: SortKey;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  align: 'left' | 'right';
}) {
  const isActive = current === sortKey;
  return (
    <th
      className={`px-4 py-3 text-[11px] font-medium text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors duration-100 ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {align === 'right' && isActive && (
          <ArrowUpDown size={11} className={`${isActive ? 'text-primary' : ''}`} />
        )}
        <span className={isActive ? 'text-primary' : ''}>{label}</span>
        {align === 'left' && isActive && (
          <ArrowUpDown size={11} className="text-primary" />
        )}
        {!isActive && <ArrowUpDown size={11} className="opacity-30" />}
      </span>
    </th>
  );
}